import React from 'react';
import withSizes from 'react-sizes';
import {CSSTransitionGroup} from 'react-transition-group';
import 'core-js/fn/array/find';

import Api from './Api';
import TagManager from './TagManager';
import Configuration from './Configuration';
import Translations, {t} from './Translations';
import Step1 from './components/steps/Step1';
import Step2 from './components/steps/Step2';
import NavTop from "./components/widgets/NavTop";
import Loader from "./components/icons/Loader";
import {mapSizesToProps} from "./utils";

// Contains the keys used to retrieves choices dependencies
const choicesDependenciesKeys = ['models', 'body_styles', 'commercial_names', 'engines', 'transmissions', 'rims'];

// Contains the keys where data are stored in state.data
const choicesDataKeys = ['model', 'bodyStyle', 'commercialName', 'engine', 'transmission', 'rims'];

export let getCode = "";
export let getLabel = "";


let vehiclesAvant, vehiclesApres, rimsUserChoice;

class App extends React.Component {

    defaultState = {
        // The current active step
        activeStep: 0,

        // Whether the next button in step is activated or not
        enableNextButton: false,

        // Whether the calculate button in step 1 (only on mobile) is activated or not
        enableCalculateButton: false,

        // Whether the app is loading vehicles data and langs
        appLoading: true,

        // Wheter the API is available or not
        serviceUnavailable: false,

        // Contains all data required by the widget except rims data and calculation data
        vehiclesData: {},


        // The data chosen by the user
        data: {
            model: ((Configuration.derivedmodel && Configuration.derivedmodel.length === 8 && !(/*@cc_on!@*/false || !!document.documentMode)) ? Configuration.derivedmodel : "")
        },

        // Store vehicle detail labels to use in vehicle detail component
        vehicleDetail: {},

        // Data returned by the average consumption calculation API
        averageConsumptionData: undefined,

        // Data returned by the usage consumption calculation API
        usageConsumptionData: undefined,

        // Whether the consumption is calculating or not
        calculatingConsumption: false,

        // Whether the rims data are loading or not
        loadingRims: false,

        // Whether to show the widget or not
        showWidget: false,

        // Contains all available choices in step 1. Updated each time the user make
        // a choice in step 1.
        step1Choices: choicesDependenciesKeys.reduce((acc, key) => ({...acc, [key]: []}), {})
    };

    baseScrollTop = 800;


    state = Object.assign({}, this.defaultState);

    /**
     * Steps wizard components
     */
    steps = [
        Step1,
        Step2
    ];

    /**
     * Add a click event listenner on the element ref found in the configuration
     */
    componentDidMount() {
        Configuration.triggerElementRef && Configuration.triggerElementRef.addEventListener('click', () => {
            this.onShowWidget()
        });
    }

    /**
     * Called whenever the consumption is calculated
     * @param consumptionData
     */
    onConsumptionChange = consumptionData => {
        this.setState({consumptionData});
    };

    /**
     * Put the application in a "service unavailable" state
     *
     * @param {object} err The error thrown
     */
    onServiceUnavailable = err => {
        this.setState({
            appLoading: false,
            loadingRims: false,
            vehiclesData: {},
            serviceUnavailable: true
        });
    };

    /**
     * Returns the LCDV16 corresponding to the user choices.
     *
     * @returns {string}
     */
    getLCDV16 = () => {
        const {vehiclesData, data} = this.state;

        // LCDV16 key is the concatenation of all user choices
        const key = [
            data.model,
            data.bodyStyle,
            data.commercialName,
            data.engine,
            data.transmission
        ].join('');

        return vehiclesData.lcdv16[key];
    };

    /**
     * Returns the step 1 choice label for the given choice value
     *
     * @param {string} key The label key
     *
     * @return {string}
     */
    getLabel = key => this.state.vehiclesData.labels[key];

    /**
     * Build the choice data array with the given choices list
     *
     * @param {object} choiceList The list of available choices in vehicles data
     *
     * @return {array} The choice data
     */
    buildChoicesData = choiceList => Object.keys(choiceList).reduce((acc, key) =>
            [...acc, {label: this.getLabel(choiceList[key]), value: choiceList[key]}]
        , []);

    /**
     * Initialize the step 1 choice by filling models choice first
     */
    initStep1Choices = () => {
        const {vehiclesData, step1Choices, data} = this.state;
        vehiclesApres = this.buildChoicesData(vehiclesData.dependencies.models).filter(vehicle => vehicle.label.indexOf('<') === -1);
        vehiclesAvant = this.buildChoicesData(vehiclesData.dependencies.models).filter(vehicle => vehicle.label.indexOf('<') !== -1);

        if(data.date !== undefined && data.date === "avant"){
            rimsUserChoice = "";
        }

        if (vehiclesApres.length === 0) {
            this.setState({
                step1Choices: {
                    ...step1Choices,
                    dates: [{label: t('Step1_form_date_avant'), value: "avant"}],
                },
                appLoading: false
            }, () => {
                if (data.model) {
                    //this.onDataChange('model', data.model);
                }
            });
            if (data.date) {
                this.onDataChange('date', data.date);
            }
        } else if (vehiclesAvant.length === 0) {

            this.setState({
                step1Choices: {
                    ...step1Choices,
                    dates: [{label: t('Step1_form_date_apres'), value: "apres"}],
                },
                appLoading: false
            }, () => {
                if (data.model) {
                    //this.onDataChange('model', data.model);
                }
            });
            if (data.date) {
                this.onDataChange('date', data.date);
            }
        } else {
            this.setState({
                step1Choices: {
                    ...step1Choices,
                    dates: [{label: t('Step1_form_date_avant'), value: "avant"}, {
                        label: t('Step1_form_date_apres'),
                        value: "apres"
                    }],
                },
                appLoading: false
            }, () => {
                if (data.model) {
                    //this.onDataChange('model', data.model);
                }
            });
            if (data.date) {
                this.onDataChange('date', data.date);
            }
        }
    };

    /**
     * Update step 1 choices, depending on user choices.
     *
     * When the user make a choice in step 1, we need to retrieve data to fill
     * the next choice if available. Data are fetched from vehiclesData.
     */
    updateStep1Choices = choiceKey => {
        let i = 0;
        const {vehiclesData, data, step1Choices} = this.state;
        const choicesList = Object.assign({}, step1Choices);
        const currentChoiceIndex = choicesDataKeys.indexOf(choiceKey);
        const nextChoiceIndex = currentChoiceIndex < choicesDataKeys.length - 1 ? (currentChoiceIndex + 1) : undefined;
        const nextChoiceDependencyKey = choicesDependenciesKeys[nextChoiceIndex];
        vehiclesApres = this.buildChoicesData(vehiclesData.dependencies.models).filter(vehicle => vehicle.label.indexOf('<') === -1);
        vehiclesAvant = this.buildChoicesData(vehiclesData.dependencies.models).filter(vehicle => vehicle.label.indexOf('<') !== -1);
      
        // filter data "models" 
        if (data.date === 'avant' && vehiclesAvant.length !== 0) {

            for (i = 0; i < vehiclesAvant.length; i++) {
                vehiclesAvant[i].label = (vehiclesAvant[i].label).substring(0, (vehiclesAvant[i].label).indexOf('<'));
            }
            // adapte conf data before traitement 
            const confLanguage = Configuration.language.toLocaleLowerCase();
            const confCountry = Configuration.country.toLocaleUpperCase();
            const confBrand = Configuration.brand.toLocaleUpperCase();

            /**
             * Traitement des fichiers plats pour la Belgique et la Swiss Traduction des véhicules 6.1 pour les pays multilingues / add prefixe
             */
            if (confCountry === 'CH' && confLanguage === 'fr' && (confBrand === 'C' || confBrand === 'P' || confBrand === 'DS')) {

                vehiclesAvant = this.buildChoicesData(vehiclesData.dependencies.models).filter(vehicle => vehicle.label.indexOf('FR') !== -1);
                for (i = 0; i < vehiclesAvant.length; i++) {
                    vehiclesAvant[i].label = (vehiclesAvant[i].label).substring(0, (vehiclesAvant[i].label).indexOf('<'));
                    vehiclesAvant[i].label = (vehiclesAvant[i].label).substring(2, vehiclesAvant[i].label.length);
                }

            } else if (confCountry === 'CH' && confLanguage === 'it' && (confBrand === 'C' || confBrand === 'P' || confBrand === 'DS')) {

                vehiclesAvant = this.buildChoicesData(vehiclesData.dependencies.models).filter(vehicle => vehicle.label.indexOf('IT') !== -1);
                for (i = 0; i < vehiclesAvant.length; i++) {
                    vehiclesAvant[i].label = (vehiclesAvant[i].label).substring(0, (vehiclesAvant[i].label).indexOf('<'));
                    vehiclesAvant[i].label = (vehiclesAvant[i].label).substring(2, vehiclesAvant[i].label.length);
                }

            } else if (confCountry === 'CH' && confLanguage === 'de' && (confBrand === 'C' || confBrand === 'P' || confBrand === 'DS')) {

                vehiclesAvant = this.buildChoicesData(vehiclesData.dependencies.models).filter(vehicle => vehicle.label.indexOf('DE') !== -1);
                for (i = 0; i < vehiclesAvant.length; i++) {
                    vehiclesAvant[i].label = (vehiclesAvant[i].label).substring(0, (vehiclesAvant[i].label).indexOf('<'));
                    vehiclesAvant[i].label = (vehiclesAvant[i].label).substring(2, vehiclesAvant[i].label.length);
                }

            } else if (confCountry === 'BE' && confLanguage === 'fr' && (confBrand === 'C' || confBrand === 'P' || confBrand === 'DS')) {

                vehiclesAvant = this.buildChoicesData(vehiclesData.dependencies.models).filter(vehicle => vehicle.label.indexOf('FR') !== -1);
                for (i = 0; i < vehiclesAvant.length; i++) {
                    vehiclesAvant[i].label = (vehiclesAvant[i].label).substring(0, (vehiclesAvant[i].label).indexOf('<'));
                    vehiclesAvant[i].label = (vehiclesAvant[i].label).substring(2, vehiclesAvant[i].label.length);
                }

            } else if (confCountry === 'BE' && confLanguage === 'nl' && (confBrand === 'C' || confBrand === 'P' || confBrand === 'DS')) {

                vehiclesAvant = this.buildChoicesData(vehiclesData.dependencies.models).filter(vehicle => vehicle.label.indexOf('NL') !== -1);
                for (i = 0; i < vehiclesAvant.length; i++) {
                    vehiclesAvant[i].label = (vehiclesAvant[i].label).substring(0, (vehiclesAvant[i].label).indexOf('<'));
                    vehiclesAvant[i].label = (vehiclesAvant[i].label).substring(2, vehiclesAvant[i].label.length);
                }
            }
            /**
             * Fin de traitement
             */

            this.setState({
                step1Choices: {
                    ...step1Choices,
                    models: vehiclesAvant
                }
            });
          
        } else if (data.date === 'apres' && vehiclesApres.length !== 0) {
            this.setState({
                step1Choices: {
                    ...step1Choices,
                    models: vehiclesApres
                }
            });
        }

        // If the current choice is the previous one from rims, update fill rims choice data
        if (nextChoiceDependencyKey === 'rims') {
            return this.updateStep1RimsChoices();
        }

        // If there is a choice list after the current one, build it depending on previous user choices
        if (nextChoiceIndex) {
            // Get dependencies key
            const dependenciesKey = choicesDataKeys.reduce((acc, key, index) =>
                acc + (index < nextChoiceIndex ? data[key] : '')
                , '');

            choicesList[nextChoiceDependencyKey] = this.buildChoicesData(vehiclesData.dependencies[nextChoiceDependencyKey][dependenciesKey]);

            if (choicesList.engines.length > 1) {
                choicesList.engines = choicesList.engines.sort((a, b) => (a.label > b.label) ? 1 : (a.label === b.label) ? ((a.code > b.code) ? 1 : -1) : -1);
            }

            this.setState({
                step1Choices: choicesList,
                enableNextButton: false
            }, () => {
                // If there is only one choice in choices list, select it automatically
                if (choicesList[nextChoiceDependencyKey].length === 1) {
                    this.onDataChange(choicesDataKeys[nextChoiceIndex], choicesList[nextChoiceDependencyKey][0].value);
                }
            });
        } else if (data.date === 'apres' && vehiclesApres.length === 1){
            this.setState({
                step1Choices: {
                    ...step1Choices,
                    models: vehiclesApres
                }
            }, ()=> {
                this.onDataChange("model", vehiclesApres[0].value);
            });
        } else if (data.date === 'avant' && vehiclesAvant.length === 1){
            this.setState({
                step1Choices: {
                    ...step1Choices,
                    models: vehiclesAvant
                }
            }, ()=> {
                this.onDataChange("model", vehiclesAvant[0].value);
            });
        }
    };

    /**
     * Fill the rims choice data
     */
    updateStep1RimsChoices = () => {
        const lcdv16 = this.getLCDV16();
        getCode = this.getLCDV16();

        if (lcdv16) {
            this.setState({loadingRims: true});

            Api
                .getFeatures(lcdv16)
                .then(data => {
                    if (data.rims.length > 1) {
                        data.rims = data.rims.sort((a, b) => (a.label > b.label) ? 1 : (a.label === b.label) ? ((a.code > b.code) ? 1 : -1) : -1);
                    }
                    this.setState({
                        loadingRims: false,

                        step1Choices: {
                            ...this.state.step1Choices,
                            rims: data.rims.map(rim => ({label: rim.label, value: rim.code})),
                        },

                        data: {
                            ...this.state.data,
                            colorExtInt: data.colorExtInt,
                            nbOptions: data.nbOptions,
                            options5C: data.options5C,
                            options7C: data.options7C
                        }
                    });

                    // If the value is unique fill it by default
                    if (this.state.step1Choices.rims.length === 1 && this.state.data.date === "avant") {
                        this.onDataChange('rims', this.state.step1Choices.rims[0].value);
                    }

                })
                .catch(err => {
                    this.onServiceUnavailable(err);
                });
        } else {
            // If no LCDV16 has been found, set rims data to an empty array
            this.setState({step1Choices: {...this.state.step1Choices, rims: []}})
        }
    };

    /**
     * Triggered when step data change.
     *
     * @param {string}  key
     * @param {*}       value
     */
    onDataChange = (key, value) => {
        if (key === "rims" && this.state.data.date !== "avant") {
            rimsUserChoice = this.state.data.rims;
            if (this.props.isMobile) {
                this.setState({enableCalculateButton: true});
            }
        }
        // If we are on the first step, make special treatment to handle choices components
        if (this.state.activeStep === 0) {
            this.onStep1DataChange(key, value);
        } else {
            this.setState({
                data: {...this.state.data, [key]: value}
            });
        }
    };

    /**
     * Triggered when step 1 data change
     */
    onStep1DataChange = (key, value) => {

        const newData = Object.assign({}, this.state.data);
        const newStep1Choices = Object.assign({}, this.state.step1Choices);
        // Retrieves the choice key index
        const choiceIndex = choicesDataKeys.indexOf(key);

        // Empty all next choices
        choicesDataKeys.forEach((choiceDataKey, choiceDataIndex) => {
            if (choiceIndex < choiceDataIndex) {
                newData[choiceDataKey] = undefined;
                newStep1Choices[choicesDependenciesKeys[choiceDataIndex]] = [];
            }
        });

        // Updates current choice value
        newData[key] = value;
        if (this.state.data.date !== "avant"){
            newData["rims"] = rimsUserChoice;
        }

        if (key === 'rims' && this.state.step1Choices.rims.length >= 1) {
            if (this.props.isMobile) {
                this.setState({enableCalculateButton: true});
            } else if ( this.state.data.date !== "avant" ) {
                this.onCalculateAverageConsumption();
                return;
            }
        }

        this.setState({
            data: newData,
            step1Choices: newStep1Choices,
            averageConsumptionData: undefined,
            vehicleDetail: {},
        }, () => {
            // If the user has selected the placeholder, no need to update
            // the choices components or calculating consumption
            if (!value) {
                return;
            }

            // If rims has been selected, calculate the consumption on desktop
            // or enable the calculate button on mobile.
            //
            // Else fill step 1 choices data.
            if (key === 'rims') {
                if (this.props.isMobile) {
                    this.setState({enableCalculateButton: true});
                } else {
                    this.onCalculateAverageConsumption();
                }
            } else {
                this.updateStep1Choices(key);
            }
        });
    };

    /**
     * Go to the previous available step.
     */
    onPrevStep = () => {
        this.changeStep(this.state.activeStep - 1);
    };

    /**
     * Go to the next available step.
     */
    onNextStep = () => {
        this.changeStep(this.state.activeStep + 1);
    };

    /**
     * Update the active step.
     *
     * @param {int} activeStep The new step
     */
    changeStep = activeStep => {
        // Ensure the given active step exists
        if (activeStep >= 0 && activeStep < this.steps.length) {
            this.setState({activeStep});
        }
    };

    /**
     * Restart the app. Use default state to reset the app
     */
    onRestart = () => {
        rimsUserChoice = "";
        this.setState({
            ...this.defaultState,
            showWidget: true,
            appLoading: false,
            vehiclesData: this.state.vehiclesData
        }, () => this.initStep1Choices());
    };

    /**
     * Return to the first step to change vehicle data
     */
    onVehicleChange = () => {
        const {isMobile} = this.props;

        this.setState({
            activeStep: 0,
            enableCalculateButton: true,
            averageConsumptionData: isMobile ? undefined : this.state.averageConsumptionData,
            usageConsumptionData: undefined
        });
    };

    /**
     * Call the average consumption calculation on the API
     */
    onCalculateAverageConsumption = () => {
        const lcdv16 = this.getLCDV16();
        const {data, step1Choices} = this.state;
        var sel = document.getElementById("rimsClass");

        //verify if type of navigation is IE and user has selected a choice
        if((/*@cc_on!@*/false || !!document.documentMode) && sel !== null) {
            var opt = sel.options[sel.selectedIndex];
            data.rims = opt.value;
            this.updateStep1RimsChoices();
        }
        const rims = step1Choices.rims.find(rims => rims.value === data.rims);

        //we added a blocks of verification to ensure the best of user experience
        //lignes : 562, 571, 572, and 577
        getLabel = (typeof rims !== 'undefined' ? rims.label : "");
        this.setState({
            calculatingConsumption: true,
            vehicleDetail: {
                model: this.getLabel(data.model),
                bodyStyle: this.getLabel(data.bodyStyle),
                commercialName: this.getLabel(data.commercialName),
                engine: this.getLabel(data.engine),
                transmission: this.getLabel(data.transmission),
                rims: (typeof rims !== 'undefined' ? rims.value : ""),
                label: (typeof rims !== 'undefined' ? rims.label : ""),
                lcdv16
            }
        });

        //we will block the call to webservice in case that rims is null
        //beacause there is no reason to call for WS MDC and we don't have a required information (rims)
        if(typeof rims !== 'undefined'){
            Api
                .calculateAverageConsumption(lcdv16, rims.label, rims.value, data.colorExtInt, data.nbOptions, data.options5C, data.options7C)
                .then(averageConsumptionData => {
                    this.setState({
                        averageConsumptionData,
                        calculatingConsumption: false,
                        enableNextButton: true
                    });
                    TagManager.sendEvent(
                        '/consommation/form/estimation-moyenne-vehicule/step-2',
                        'lead page',
                        'cold lead',
                        'consommation estimation moyenne',
                        2,
                        'estimation moyenne',
                        this.state.data.bodyStyle,
                        `${this.getLabel(data.model)} ${this.getLabel(data.bodyStyle)} ${this.getLabel(data.commercialName)} ${this.getLabel(data.engine)}`
                    )
                })
                .catch(err => {
                    this.onServiceUnavailable(err);
                });
        }
    };

    /**
     * Call the usage consumption calculation on the API
     */
    onCalculateUsageConsumption = () => {
        const lcdv16 = this.getLCDV16();
        const {data, averageConsumptionData} = this.state;

        this.setState({
            calculatingConsumption: true,
            enableNextButton: false
        });

        Api
            .calculateUsageConsumption(data, averageConsumptionData, lcdv16)
            .then(usageConsumptionData => {
                this.setState({
                    usageConsumptionData,
                    calculatingConsumption: false,
                    enableNextButton: true
                });

                TagManager.sendEvent(
                    '/consommation/form/estimation-precise-usage/step-5',
                    'lead page',
                    'cold lead',
                    'consommation estimation precise',
                    5,
                    'estimation precise',
                    this.state.data.bodyStyle,
                    `${this.getLabel(data.model)} ${this.getLabel(data.bodyStyle)} ${this.getLabel(data.commercialName)} ${this.getLabel(data.engine)}`
                )
            })
            .catch(err => {
                this.onServiceUnavailable(err);
            });
    };

    /**
     * Close the widget without reseting vehicles data
     */
    onCloseWidget = () => {
        window.scrollTo(0, this.baseScrollTop);
        document.body.classList.remove('cccu-opened');

        this.setState({
            ...this.defaultState,
            appLoading: false,
            serviceUnavailable: this.state.serviceUnavailable,
            //vehiclesData: this.state.vehiclesData
        }, () => !this.state.serviceUnavailable);
    };

    /**
     * Open the widget
     */
    onShowWidget = () => {
        this.baseScrollTop = window.scrollY;
        document.body.classList.add('cccu-opened');

        // Show the widget. If no vehicles data has been set, init the widget
        // for the first time.
        this.setState({
            showWidget: true,
            serviceUnavailable: false

        }, () => {
            if (Object.keys(this.state.vehiclesData).length === 0) {
                this.setState({appLoading: true});

                Promise.all([
                    Api.getVehicles(),
                    Translations.load(Configuration.country, Configuration.language)
                ])
                    .then(values => {
                        if (this.defaultState.data.model !== "") {
                            if (Object.values(values[0].dependencies.models).indexOf(this.defaultState.data.model) === -1) {
                                this.defaultState.data.model = "";
                            }
                        }
                        this.setState({
                                vehiclesData: values[0]
                            }, () => this.initStep1Choices()
                        );
                        if (this.state.step1Choices.dates.length === 1) {
                            this.onDataChange('date', this.state.step1Choices.dates[0].value);
                        }
                        if (this.defaultState.data.model !== "") {
                            if (this.state.step1Choices.dates.length === 2) {
                                this.onDataChange('date', this.state.step1Choices.dates[1].value);
                                this.onDataChange('model', this.defaultState.data.model);
                            } else if (this.state.step1Choices.dates.length === 1 && vehiclesAvant === undefined) {
                                this.onDataChange('date', this.state.step1Choices.dates[0].value);
                                this.onDataChange('model', this.defaultState.data.model);
                            }
                        }
                    })
                    .catch(err => {
                        this.onServiceUnavailable(err);
                    });
            } else {
                this.initStep1Choices();
            }
        });
    };

    render() {
        const {
            appLoading,
            serviceUnavailable,
            showWidget,
            loadingRims,
            enableNextButton,
            enableCalculateButton,
            activeStep,
            data,
            vehicleDetail,
            averageConsumptionData,
            usageConsumptionData,
            calculatingConsumption,
            step1Choices
        } = this.state;

        return (
            <CSSTransitionGroup
                transitionName="widget"
                transitionEnterTimeout={300}
                transitionLeaveTimeout={250}
            >
                {showWidget &&
                <div className="container-fluid">
                    {appLoading ? (
                        <div className="row">
                            <div className="col-12 loader-container">
                                <Loader className="loader"/>
                            </div>
                        </div>
                    ) : (
                        <React.Fragment>
                            {serviceUnavailable ? (
                                <div className="row">
                                    <div className="col-12">
                                        <div className="jumbotron mt-4 text-center">
                                            <h2>{t('Service_unavailable_error')}</h2>

                                            <button className="btn btn-primary" onClick={this.onCloseWidget}>
                                                {t('Service_unavailable_error_close')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <React.Fragment>
                                    <NavTop client={client(Configuration.brand)} activeStep={activeStep} onClose={this.onCloseWidget}/>

                                    {this.steps.map((StepComponent, key) => {
                                        if (activeStep !== key) {
                                            return null;
                                        }

                                        return React.cloneElement(<StepComponent/>, {
                                            key,
                                            data,
                                            vehicleDetail,
                                            loadingRims,
                                            averageConsumptionData,
                                            usageConsumptionData,
                                            calculatingConsumption,
                                            enableNextButton,
                                            enableCalculateButton,
                                            choices: step1Choices,
                                            onPrevStep: this.onPrevStep,
                                            onNextStep: this.onNextStep,
                                            onRestart: this.onRestart,
                                            onDataChange: this.onDataChange,
                                            onVehicleChange: this.onVehicleChange,
                                            onCalculateAverageConsumption: this.onCalculateAverageConsumption,
                                            onCalculateUsageConsumption: this.onCalculateUsageConsumption
                                        })
                                    })}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    )}
                </div>
                }
            </CSSTransitionGroup>
        );
    }
}
const client=(brand)=>{
    console.log(brand);
    return ("undefined" !== typeof brand && brand === "P" ? " ap":"");
};
export default withSizes(mapSizesToProps)(App);


// WEBPACK FOOTER //
// ./src/App.js